import React from 'react';
import { RefreshCw, ArrowDownToLine, ExternalLink, GitBranch, Copy } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { providerOf, can } from '../../services/git/providers';
import { describeSync, ago } from '../../services/git/gitSyncStatus';
import { openExternal } from '../../utils/openExternal';
import { copyTextToClipboard } from '../../utils/clipboard';
import ProviderIcon from './ProviderIcon';
import GitDetailHead, { MetaLine } from './GitDetailHead';
import GitMoreMenu from './GitMoreMenu';

/*
 * The detail card's head for a WORKSPACE (Git page redesign, 05.10.2026):
 * synced to a remote (LinkedRow), a folder that is its own repository
 * (FolderRow), or not in a repository yet (UnlinkedRow — the card below it
 * offers Connect). Names and test ids are the rows' they replaced.
 */

/** Where it syncs, its branch and when it last did: the head's meta line. */
export function remoteMeta(git, entry, providerId, label) {
  const { owner, repo, branch } = git || {};
  const synced = entry?.lastSyncAt && !String(label).startsWith('Synced') ? `Last synced ${ago(entry.lastSyncAt)}` : null;
  return (
    <MetaLine
      parts={[
        <span key="repo" className="git-dhead-repo">
          <ProviderIcon id={providerId} size={13} />
          <span className="git-mono">{owner && repo ? `${owner}/${repo}` : git?.url}</span>
        </span>,
        branch && (
          <span key="branch" className="git-dhead-branch">
            <GitBranch size={11} />
            {branch}
          </span>
        ),
        synced,
      ]}
    />
  );
}

/** Sync now (or Retry) · Pull · Open on … — the actions of anything synced to a remote. */
export function RemoteActions({ provider, git, busy, tone, onSync, onPull }) {
  const web = provider && can(provider, 'openInBrowser') ? provider.webUrl(git) : '';
  return (
    <>
      <button type="button" className="git-btn git-btn--primary" disabled={busy || !provider} onClick={onSync}>
        <RefreshCw size={13} />
        {tone === 'error' ? 'Retry' : 'Sync now'}
      </button>
      <Tooltip content="Get changes from the remote" side="bottom">
        <button type="button" className="git-btn git-btn--ghost" disabled={busy || !provider} onClick={onPull} aria-label="Pull">
          <ArrowDownToLine size={13} />
          Pull
        </button>
      </Tooltip>
      {web && (
        <Tooltip content={`Open on ${provider.label}`} side="bottom">
          <button type="button" className="git-btn git-btn--icon" onClick={() => openExternal(web)} aria-label={`Open on ${provider.label}`}>
            <ExternalLink size={14} />
          </button>
        </Tooltip>
      )}
    </>
  );
}

/** The "…" entry every remote link has: its address, to paste into a terminal or another tool. */
export const copyAddressItem = (git) =>
  git?.url && { icon: <Copy size={14} />, label: 'Copy repository address', onClick: () => copyTextToClipboard(git.url) };

function safeProvider(target) {
  try {
    return providerOf(target);
  } catch {
    return null; // a link from a newer build: shown, but not actionable
  }
}

/** A workspace that syncs to a remote: where, its state, and what can be done. */
export function LinkedRow({ workspace, entry, syncing, onSync, onPull }) {
  const provider = safeProvider(workspace);
  const sync = describeSync(entry, { syncing });
  return (
    <GitDetailHead
      name={workspace.name}
      tone={sync.tone}
      status={sync.label}
      rootProps={{ 'data-testid': 'git-linked-row', 'data-git-ws': workspace.id }}
      meta={remoteMeta(workspace.git, entry, provider?.id, sync.label)}
      notes={
        // Only a reason the pill does not already say ("Sync failed" twice: Sandbox, card ccdcea2d).
        sync.tone === 'error' && entry?.lastError && entry.lastError !== sync.label ? (
          <span className="git-row-error">{entry.lastError}</span>
        ) : null
      }
      actions={
        <>
          <RemoteActions
            provider={provider}
            git={workspace.git}
            busy={syncing}
            tone={sync.tone}
            onSync={() => onSync(workspace)}
            onPull={() => onPull(workspace)}
          />
          <GitMoreMenu items={[copyAddressItem(workspace.git)]} />
        </>
      }
    />
  );
}

/** A workspace opened from a folder that is its own repository. */
export function FolderRow({ workspace }) {
  return (
    <GitDetailHead
      name={workspace.name}
      tone="idle"
      status="Your own folder"
      rootProps={{ 'data-testid': 'git-folder-row', 'data-git-ws': workspace.id }}
      meta={<span className="git-mono git-row-path">{workspace.rootPath}</span>}
    />
  );
}

/** A workspace without git: its head; the card below offers Connect (GitDetail wraps both as git-unlinked-row). */
export function UnlinkedRow({ workspace }) {
  return (
    <GitDetailHead
      name={workspace.name}
      tone="off"
      status="Not connected"
      meta={<span>Only on this device and your Apiboo account</span>}
    />
  );
}
